// Talks to the Worker leaderboard API. Best-effort + fire-and-forget: any failure (e.g.
// running under `vite dev` with no Worker) degrades gracefully - the game never breaks.
// A session token is fetched at run start so the submitted run time is bounded by it.

export interface LeaderEntry {
  name: string;
  score: number;
  t: number;
  country: string | null;
  avatar: number;
}

export interface SubmitResult {
  ok: boolean;
  rank?: number | null;
  error?: string;
}

interface Session {
  sessionId: string;
  issuedAt: number;
  token: string;
}

export type LeaderboardClientOptions = {
  native?: boolean;
  onNativeLeaderboard?: () => void;
};

export class LeaderboardClient {
  private session: Session | null = null;
  private readonly native: boolean;
  private readonly onNativeLeaderboard?: () => void;

  constructor(options: LeaderboardClientOptions = {}) {
    this.native = options.native ?? false;
    this.onNativeLeaderboard = options.onNativeLeaderboard;
  }

  get usesNativeLeaderboard(): boolean {
    return this.native;
  }

  openNativeLeaderboard(): void {
    if (this.native) this.onNativeLeaderboard?.();
  }

  /** Fetch a fresh session token for a new run (best-effort). */
  async startSession(): Promise<void> {
    this.session = null;
    if (this.native) return;
    try {
      const r = await fetch('/api/session');
      const d = (await r.json()) as { ok?: boolean; sessionId?: string; issuedAt?: number; token?: string };
      if (d.ok && d.sessionId && typeof d.issuedAt === 'number' && d.token) {
        this.session = { sessionId: d.sessionId, issuedAt: d.issuedAt, token: d.token };
      }
    } catch {
      /* no backend (vite dev / offline) - leaderboard disabled this run */
    }
  }

  hasSession(): boolean {
    return this.session !== null;
  }

  /** Submit a run. `elapsedMs` is the in-game run duration. country '' = let the server detect. */
  async submit(name: string, score: number, country: string, avatar: number, elapsedMs: number): Promise<SubmitResult> {
    if (this.native) return { ok: false, error: 'native' };
    if (this.session === null) return { ok: false, error: 'offline' };
    const payload = { ...this.session, name, score, country: country || undefined, avatar, elapsedMs };
    this.session = null; // one-use
    try {
      const r = await fetch('/api/score', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const d = (await r.json()) as SubmitResult;
      return d.ok ? { ok: true, rank: d.rank ?? null } : { ok: false, error: d.error ?? 'rejected' };
    } catch {
      return { ok: false, error: 'network' };
    }
  }

  async fetchLeaderboard(): Promise<LeaderEntry[]> {
    if (this.native) return [];
    try {
      const r = await fetch('/api/leaderboard', { cache: 'no-store' }); // always current (esp. post-submit)
      const d = (await r.json()) as { ok?: boolean; entries?: LeaderEntry[] };
      return d.ok && Array.isArray(d.entries) ? d.entries : [];
    } catch {
      return [];
    }
  }
}

// --- country helpers ---

/** Best-guess default country from the browser locale (e.g. "en-US" → "US"). */
export function defaultCountry(): string {
  const langs = navigator.languages ?? [navigator.language];
  for (const l of langs) {
    const m = /[-_]([A-Za-z]{2})\b/.exec(l ?? '');
    if (m) return m[1].toUpperCase();
  }
  return '';
}

/** Flag emoji from an ISO 3166-1 alpha-2 code (regional indicator letters). */
export function flagEmoji(country: string | null): string {
  if (!country || !/^[A-Za-z]{2}$/.test(country)) return '🏳️';
  const base = 0x1f1e6 - 'A'.charCodeAt(0);
  return String.fromCodePoint(base + country.toUpperCase().charCodeAt(0), base + country.toUpperCase().charCodeAt(1));
}

// A practical subset for the manual picker (Auto-detect covers everything else server-side).
// prettier-ignore
export const COUNTRIES: ReadonlyArray<readonly [string, string]> = [
  ['US', 'United States'], ['GB', 'United Kingdom'], ['CA', 'Canada'], ['AU', 'Australia'],
  ['DE', 'Germany'], ['FR', 'France'], ['IT', 'Italy'], ['ES', 'Spain'], ['PT', 'Portugal'],
  ['NL', 'Netherlands'], ['BE', 'Belgium'], ['SE', 'Sweden'], ['NO', 'Norway'], ['DK', 'Denmark'],
  ['FI', 'Finland'], ['PL', 'Poland'], ['RO', 'Romania'], ['GR', 'Greece'], ['CH', 'Switzerland'],
  ['AT', 'Austria'], ['IE', 'Ireland'], ['CZ', 'Czechia'], ['HU', 'Hungary'], ['UA', 'Ukraine'],
  ['RU', 'Russia'], ['TR', 'Turkey'], ['BR', 'Brazil'], ['MX', 'Mexico'], ['AR', 'Argentina'],
  ['CL', 'Chile'], ['CO', 'Colombia'], ['JP', 'Japan'], ['KR', 'South Korea'], ['CN', 'China'],
  ['IN', 'India'], ['ID', 'Indonesia'], ['PH', 'Philippines'], ['VN', 'Vietnam'], ['TH', 'Thailand'],
  ['SG', 'Singapore'], ['MY', 'Malaysia'], ['ZA', 'South Africa'], ['EG', 'Egypt'], ['NG', 'Nigeria'],
  ['SA', 'Saudi Arabia'], ['AE', 'UAE'], ['IL', 'Israel'], ['NZ', 'New Zealand'],
];
